import React, { useEffect, useMemo } from 'react'
import { Fade, Grid } from '@mui/material'
import { useQueryClient } from 'react-query'
import TablePanel from '../Support/TablePanel'
import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import {
    DataTable,
    DataTableOpenPanelButton,
    DataTableSection,
    DataTableText,
    DataTableToolbar,
    getNoMatchesText,
    useDataTableFilters,
    useDataTableSearchState,
} from '../../shared/components/DataTable'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../shared/hooks/useIsSnapshotMode'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import {
    getStrategicImperativesQueryKey,
    StrategicImperative,
    useCreateStrategicImperativeMutation,
    useReorderStrategicImperativesMutation,
    useStrategicImperativesQuery,
} from '../../shared/queries/strategicImperatives'
import { useStrategy } from '../../shared/hooks/useStrategy'
import {
    getStrategicImperativeSearchValues,
    STRATEGIC_IMPERATIVE_COLUMNS,
    StrategicImperativeColumnId,
} from './StrategicImperativeTable.constants'
import { useTableDetailPanel } from '../../shared/hooks/useTableDetailPanel'

type StrategicImperativeTableProps = {
    newFormData?: Record<string, unknown>
}

const StrategicImperativeTable = ({ newFormData }: StrategicImperativeTableProps) => {
    const { singular, plural } = useStrategicImperativeDisplayName()

    const queryClient = useQueryClient()
    const strategyId = useStrategy()?._id

    const isSnapshotMode = useIsSnapshotMode()
    const isRoadmapReadonly = useIsRoadmapReadonly()

    const { data, isLoading, isError } = useStrategicImperativesQuery()
    const strategicImperatives = useMemo(() => data ?? [], [data])

    const { mutate: createStrategicImperative } = useCreateStrategicImperativeMutation()
    const { mutate: reorderStrategicImperatives } = useReorderStrategicImperativesMutation()

    const searchState = useDataTableSearchState()

    const {
        openRow: openStrategicImperative,
        navigationStack,
        openPanel,
        closePanel,
    } = useTableDetailPanel(strategicImperatives, 'Strategic Imperative')

    const { toolbarProps, hasActiveFilters, visibleRows } = useDataTableFilters({
        rows: strategicImperatives,
        filterDefinitions: [],
        getSearchValues: getStrategicImperativeSearchValues,
        state: searchState,
    })

    useEffect(() => {
        if (newFormData && Object.keys(newFormData).length) {
            createStrategicImperative(newFormData)
        }
    }, [newFormData, createStrategicImperative])

    const renderCell = (strategicImperative: StrategicImperative, columnId: StrategicImperativeColumnId) =>
        columnId === 'name' ? (
            <DataTableText
                text={strategicImperative.name}
                variant="16x600"
                color="figma.grayscale.textHier1"
                maxLines={3}
            />
        ) : null

    return (
        <Fade in timeout={800}>
            <Grid container item xs>
                <DataTableSection isLoading={isLoading} isError={isError} entityLabel={plural} loadingTitle={plural}>
                    <DataTable
                        showIndexColumn
                        columns={STRATEGIC_IMPERATIVE_COLUMNS}
                        rows={visibleRows}
                        getRowId={(strategicImperative) => strategicImperative._id}
                        renderCell={renderCell}
                        renderRowActions={(strategicImperative) => (
                            <DataTableOpenPanelButton
                                accessibleLabel={`Open ${strategicImperative.name}`}
                                onClick={() => openPanel(strategicImperative._id)}
                            />
                        )}
                        reorder={{
                            isDisabled: isSnapshotMode || isRoadmapReadonly || hasActiveFilters,
                            onReorder: reorderStrategicImperatives,
                        }}
                        toolbar={<DataTableToolbar {...toolbarProps} searchPlaceholder={`${singular} Search`} />}
                        emptyState={
                            <EmptyPlaceholder
                                title={
                                    hasActiveFilters
                                        ? getNoMatchesText(plural.toLowerCase())
                                        : `No ${plural} to display`
                                }
                                subtitle={
                                    hasActiveFilters
                                        ? 'Clear the search to see every row again.'
                                        : `Click 'Add New' on the left panel to create a new ${singular}`
                                }
                                sx={{ minHeight: 280 }}
                            />
                        }
                    />
                </DataTableSection>
                {!!openStrategicImperative && (
                    <TablePanel
                        data={openStrategicImperative}
                        type="strategicImperative"
                        closePanel={closePanel}
                        openPanelAction
                        updateParent={() => queryClient.invalidateQueries(getStrategicImperativesQueryKey(strategyId))}
                        navigationStack={navigationStack}
                    />
                )}
            </Grid>
        </Fade>
    )
}

export default StrategicImperativeTable
